import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Widget from '../components/Widget';
import DirectoryHeader from '../components/DirectoryHeader';
import { getWidgets } from '../services/widgetService';
import { getPublishedBlogs } from '../services/blogService';

const CATEGORIES = {
  people: {
    title: 'Chess People — Creators, Coaches, Players & More',
    description: 'Discover chess creators, coaches, streamers, players, and educators in the chess community.',
    tagFilter: 'people',
    heading: 'People in Chess'
  },
  play: {
    title: 'Play Chess Online',
    description: 'Find the best places to play chess online. Compare platforms, apps, and tools.',
    tagFilter: 'play',
    heading: 'Play Chess Online'
  },
  learn: {
    title: 'Chess Learning Platforms & Tools',
    description: 'Chess learning resources, courses, and training tools to improve your skills.',
    tagFilter: 'learn',
    heading: 'Learning Platforms'
  },
  analysis: {
    title: 'Chess Analysis Tools & Engines',
    description: 'Top chess analysis tools, engines, and software for analyzing games and positions.',
    tagFilter: 'analysis',
    heading: 'Engine Analysis'
  },
  openings: {
    title: 'Chess Opening Explorers & Databases',
    description: 'Opening databases, explorers, and preparation tools for mastering chess openings.',
    tagFilter: 'openings',
    heading: 'Opening Preparation'
  },
  books: {
    title: 'Chess Books & Reading',
    description: 'Chess books and reading resources for players of all levels.',
    tagFilter: 'books',
    heading: 'Explore Chess Books'
  },
  events: {
    title: 'Chess Events',
    description: 'Chess tournaments, events, and competitions. Find platforms for competitive play.',
    tagFilter: 'events',
    heading: 'Chess Events'
  },
  new: {
    title: 'New Chess Apps & Tools',
    description: 'Recently added chess apps, tools, and platforms on TrendingChess.',
    tagFilter: '_isNew',
    heading: 'Recently Added'
  }
};

const CATEGORY_ORDER = ['people', 'play', 'learn', 'analysis', 'openings', 'books', 'events', 'new'];

const PINNED_WIDGETS = {
  people: 'Charlize van Zyl',
  openings: 'Opening Tree',
  analysis: 'Chessiro',
  play: 'Lichess',
  learn: 'ChessMood',
  pros: '2700Chess',
  books: 'Forward Chess'
};

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const CategoryPage = ({ category }) => {
  const [widgets, setWidgets] = useState([]);
  const [allWidgets, setAllWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [blogs, setBlogs] = useState([]);

  const config = CATEGORIES[category];

  useEffect(() => {
    const fetchAndFilter = async () => {
      setLoading(true);
      try {
        const fetchedWidgets = await getWidgets();
        const visible = fetchedWidgets.filter(w => !w.hidden && !w.excludeFromAll);
        setAllWidgets(visible);

        let filtered;
        if (config.tagFilter === '_isNew') {
          filtered = visible.filter(w => w.isNew);
        } else if (config.tagFilter === 'people') {
          // Match 'people' and legacy tags 'creators'/'professionals'
          const peopleTags = ['people', 'creators', 'professionals'];
          filtered = visible.filter(w =>
            Array.isArray(w.tag) ? w.tag.some(t => peopleTags.includes(t)) : peopleTags.includes(w.tag)
          );
        } else {
          filtered = visible.filter(w =>
            Array.isArray(w.tag) ? w.tag.includes(config.tagFilter) : w.tag === config.tagFilter
          );
        }

        // Pin featured widget first, shuffle the rest
        const pinnedTitle = PINNED_WIDGETS[category];
        const pinned = pinnedTitle ? filtered.find(w => w.title === pinnedTitle) : null;
        const rest = pinned ? filtered.filter(w => w.title !== pinnedTitle) : filtered;
        const ordered = pinned ? [pinned, ...shuffleArray(rest)] : shuffleArray(filtered);

        setWidgets(ordered);
      } catch (err) {
        console.error('Error fetching widgets for category:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndFilter();
  }, [category, config.tagFilter]);

  // Fetch blogs for search results
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const publishedBlogs = await getPublishedBlogs();
        if (publishedBlogs) setBlogs(publishedBlogs);
      } catch (err) {
        console.error('Failed to fetch blogs:', err);
      }
    };
    fetchBlogs();
  }, []);

  // Search filters across ALL widgets (not just current category)
  const getSearchResults = () => {
    if (!isSearchMode || !searchQuery) return { widgets: [], blogs: [] };
    const q = searchQuery.toLowerCase();
    const matchedWidgets = allWidgets.filter(w =>
      (w.title && w.title.toLowerCase().includes(q)) ||
      (w.subtitle && w.subtitle.toLowerCase().includes(q))
    );
    const matchedBlogs = blogs.filter(b =>
      b.title && b.title.toLowerCase().includes(q)
    );
    return { widgets: matchedWidgets, blogs: matchedBlogs };
  };

  const searchResults = getSearchResults();

  if (!config) return null;

  const canonicalUrl = `https://trendingchess.com/${category}`;

  return (
    <>
      <DirectoryHeader
        activeCategory={category}
        onSearchChange={(query) => {
          if (query) {
            setIsSearchMode(true);
            setSearchQuery(query);
          } else {
            setIsSearchMode(false);
            setSearchQuery('');
          }
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="max-w-6xl mx-auto px-4 pt-32 pb-12"
      >
        <Helmet>
          <title>{config.title} | TrendingChess.com</title>
          <meta name="description" content={config.description} />
          <meta property="og:title" content={`${config.title} | TrendingChess.com`} />
          <meta property="og:description" content={config.description} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>

        {/* Page Heading */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">
            {config.heading}
          </h1>
          <p className="text-gray-400 text-lg">
            {config.description}
          </p>
        </div>

        {/* Widget Grid */}
        {loading ? (
          <div className="text-gray-400 text-center py-12">Loading...</div>
        ) : isSearchMode ? (
          // Search results mode
          <div className="mb-12">
            {!searchQuery ? (
              <div className="text-gray-400 text-center py-12">looking for something?</div>
            ) : searchResults.widgets.length === 0 && searchResults.blogs.length === 0 ? (
              <div className="text-gray-400 text-center py-12">no results found...</div>
            ) : (
              <>
                {searchResults.widgets.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 mb-8">
                    {searchResults.widgets.map((item) => (
                      <Widget
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        subtitle={item.subtitle}
                        icon={item.icon ? (
                          <img src={item.icon} alt={item.title} className="w-14 h-14 rounded-xl object-cover" />
                        ) : undefined}
                        link={item.link}
                        hidden={item.hidden}
                        isNew={item.isNew}
                        verified={item.verified}
                        links={item.links}
                        backgroundImage={item.backgroundImage}
                        slug={item.slug}
                        titled={item.titled}
                        chessTitle={item.chessTitle}
                      />
                    ))}
                  </div>
                )}
                {searchResults.blogs.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-300 mb-4">Blog Posts</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {searchResults.blogs.map((blog) => (
                        <Link
                          key={blog.id}
                          to={`/blog/${blog.slug || blog.id}`}
                          className="block bg-gray-800 border border-gray-700 rounded-lg overflow-hidden hover:border-yellow-400 transition-colors"
                        >
                          {blog.featuredImage && (
                            <div className="aspect-video overflow-hidden">
                              <img src={blog.featuredImage} alt={blog.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="p-4">
                            <h4 className="text-yellow-400 font-semibold mb-1 line-clamp-2">{blog.title}</h4>
                            {blog.metaDescription && (
                              <p className="text-gray-400 text-sm line-clamp-2">{blog.metaDescription}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : widgets.length === 0 ? (
          <div className="text-gray-400 text-center py-12">No listings found in this category yet.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 mb-12">
            {widgets.map((item) => (
              <Widget
                key={item.id}
                id={item.id}
                title={item.title}
                subtitle={item.subtitle}
                icon={item.icon ? (
                  <img src={item.icon} alt={item.title} className="w-14 h-14 rounded-xl object-cover" />
                ) : undefined}
                link={item.link}
                hidden={item.hidden}
                isNew={item.isNew}
                verified={item.verified}
                links={item.links}
                backgroundImage={item.backgroundImage}
                slug={item.slug}
                titled={item.titled}
                chessTitle={item.chessTitle}
              />
            ))}
          </div>
        )}

        {/* Browse Other Categories */}
        <div className="border-t border-gray-700 pt-8">
          <h2 className="text-xl font-bold text-gray-300 mb-4">Browse Other Categories</h2>
          <div className="flex flex-wrap gap-3">
            {CATEGORY_ORDER.filter(c => c !== category).map(c => (
              <Link
                key={c}
                to={`/${c}`}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-full text-gray-300 hover:text-yellow-400 hover:border-yellow-400 transition-colors capitalize"
              >
                {CATEGORIES[c].heading}
              </Link>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default CategoryPage;
